import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../services/api';
import SessionManager from '../utils/sessionManager';
import SuperAdminLayout from '../components/layouts/SuperAdminLayout';
import Icon from '../components/atoms/Icon';

const SuperAdminPaymentManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState('month');
  
  // Conversion rate
  const [conversionRate, setConversionRate] = useState(1.0);
  const [newRate, setNewRate] = useState('');
  const [rateLoading, setRateLoading] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);

  // Get admin ID from session
  const currentSession = SessionManager.getCurrentSession();
  const adminId = currentSession?.user_id || currentSession?.profile?.id;

  // Check authentication
  useEffect(() => {
    if (!currentSession || currentSession.role !== 'super_admin') {
      navigate('/');
    }
  }, [currentSession, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [period]);

  useEffect(() => {
    fetchConversionRate();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await request(`/super-admin/payments/dashboard?admin_id=${adminId}&period=${period}`);
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversionRate = async () => {
    try {
      const data = await request(`/super-admin/payments/conversion-rate?admin_id=${adminId}`);
      setConversionRate(data.inr_to_credit_rate);
      setNewRate(data.inr_to_credit_rate.toString());
    } catch (error) {
      console.error('Failed to fetch rate:', error);
    }
  };

  const updateConversionRate = async () => {
    if (!newRate || parseFloat(newRate) <= 0) {
      alert('Please enter a valid rate');
      return;
    }

    try {
      setRateLoading(true);
      const data = await request(
        `/super-admin/payments/conversion-rate?admin_id=${adminId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ inr_to_credit_rate: parseFloat(newRate) })
        }
      );
      alert(data.message);
      setConversionRate(data.new_rate);
      setShowRateModal(false);
      fetchDashboard();
    } catch (error) {
      alert(error.message || 'Failed to update rate');
    } finally {
      setRateLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const StatCard = ({ title, value, subtitle, icon, color }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon name={icon} className="text-white text-xl" />
        </div>
      </div>
    </div>
  );

  return (
    <SuperAdminLayout>
      <div className="h-full overflow-auto bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payment Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Manage Razorpay payments and conversion rates</p>
            </div>
            <button
              onClick={() => {
                setNewRate(conversionRate.toString());
                setShowRateModal(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Icon name="edit" className="mr-2 text-lg" />
              Update Rate
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Period Filter */}
          <div className="flex items-center space-x-2 mb-6">
            {[
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'all', label: 'All Time' }
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === p.value
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            </div>
          ) : dashboard ? (
            <div className="space-y-6">
              {/* Main Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total Revenue"
                  value={formatCurrency(dashboard.total_revenue_inr)}
                  icon="account_balance"
                  color="bg-gradient-to-br from-green-500 to-green-600"
                />
                <StatCard
                  title="Credits Sold"
                  value={dashboard.total_credits_sold.toLocaleString()}
                  subtitle="Total credits purchased"
                  icon="stars"
                  color="bg-gradient-to-br from-blue-500 to-blue-600"
                />
                <StatCard
                  title="Total Transactions"
                  value={dashboard.total_transactions}
                  subtitle={`${dashboard.successful_transactions} successful`}
                  icon="receipt"
                  color="bg-gradient-to-br from-purple-500 to-purple-600"
                />
                <StatCard
                  title="Avg Transaction"
                  value={formatCurrency(dashboard.average_transaction_value)}
                  icon="trending_up"
                  color="bg-gradient-to-br from-orange-500 to-orange-600"
                />
              </div>

              {/* Transaction Status */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Successful</p>
                      <p className="text-3xl font-bold text-green-600">{dashboard.successful_transactions}</p>
                      <p className="text-xs text-gray-500 mt-1">Completed payments</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon name="check_circle" className="text-green-600 text-2xl" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Failed</p>
                      <p className="text-3xl font-bold text-red-600">{dashboard.failed_transactions}</p>
                      <p className="text-xs text-gray-500 mt-1">Payment failures</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Icon name="cancel" className="text-red-600 text-2xl" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Pending</p>
                      <p className="text-3xl font-bold text-yellow-600">{dashboard.pending_transactions}</p>
                      <p className="text-xs text-gray-500 mt-1">Awaiting confirmation</p>
                    </div>
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Icon name="schedule" className="text-yellow-600 text-2xl" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Rate Banner */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center mb-2">
                      <Icon name="currency_rupee" className="mr-2 text-xl" />
                      <p className="text-blue-100 text-sm font-medium">Current Conversion Rate</p>
                    </div>
                    <p className="text-3xl font-bold">1 Credit = {formatCurrency(dashboard.current_conversion_rate)}</p>
                    <p className="text-blue-100 text-xs mt-2">This rate applies to all new purchases</p>
                  </div>
                  <button
                    onClick={() => {
                      setNewRate(conversionRate.toString());
                      setShowRateModal(true);
                    }}
                    className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-md"
                  >
                    Update Rate
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Rate Update Modal */}
      {showRateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Update Conversion Rate</h2>
              <button
                onClick={() => setShowRateModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="close" className="text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="info" className="text-blue-600 mr-2" />
                  <div>
                    <p className="text-sm text-blue-900 font-medium">
                      Current Rate: 1 Credit = {formatCurrency(conversionRate)}
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Existing orders will not be affected
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Rate (INR per Credit)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="1000"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1.0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Range: ₹0.01 to ₹1000 per credit
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                  <Icon name="calculate" className="mr-1 text-sm" />
                  Examples:
                </p>
                <div className="space-y-1 text-xs text-gray-600">
                  <p>• 100 Credits = {formatCurrency((parseFloat(newRate) || 1) * 100)}</p>
                  <p>• 500 Credits = {formatCurrency((parseFloat(newRate) || 1) * 500)}</p>
                  <p>• 1000 Credits = {formatCurrency((parseFloat(newRate) || 1) * 1000)}</p>
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => setShowRateModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={updateConversionRate}
                  disabled={rateLoading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rateLoading ? 'Updating...' : 'Update Rate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminPaymentManagement;
